import * as React from "react";
import { useTranslation } from "react-i18next";
import {
  Navbar,
  Hero,
  Stats,
  Features,
  HowItWorks,
  Benefits,
  Testimonials,
  Pricing,
  FAQ,
  FinalCTA,
  Footer,
  AppScreenshots,
  FeatureSpotlight,
  AppDemoVideo,
  AppStoreDownload,
  IntegrationLogos,
} from "@/components/landing";

import type { SeedData, SectionKey } from "@/types/seed-data";
import seedDataJson from "../../../seed-data.json";

// Type the imported seed data
const seedData = seedDataJson as SeedData;

/**
 * Section Component Mapping
 *
 * Maps section keys to their React components and structural prop extraction.
 * Text content is handled by each component via useTranslation.
 */
const sectionComponents: Record<
  SectionKey,
  {
    Component: React.ComponentType<any>;
    getProps: (data: SeedData) => Record<string, unknown> | null;
    hasData: (data: SeedData) => boolean;
  }
> = {
  stats: {
    Component: Stats,
    getProps: (data) => ({ stats: data.stats }),
    hasData: (data) => Boolean(data.stats && data.stats.length > 0),
  },
  sponsors: {
    // Placeholder - Sponsors component not yet implemented
    Component: () => null,
    getProps: () => null,
    hasData: () => false,
  },
  features: {
    Component: Features,
    // Only pass structural data — ids and icons
    getProps: (data) => ({
      features: data.features,
    }),
    hasData: (data) => Boolean(data.features && data.features.length > 0),
  },
  blog: {
    // Placeholder - Blog component not yet implemented
    Component: () => null,
    getProps: () => null,
    hasData: () => false,
  },
  howItWorks: {
    Component: HowItWorks,
    getProps: (data) => ({
      steps: data.howItWorks?.steps,
    }),
    hasData: (data) => Boolean(data.howItWorks?.steps && data.howItWorks.steps.length > 0),
  },
  benefits: {
    Component: Benefits,
    getProps: () => ({}),
    hasData: () => true,
  },
  testimonials: {
    Component: Testimonials,
    getProps: (data) => ({
      testimonials: data.testimonials?.items,
    }),
    hasData: (data) => Boolean(data.testimonials?.items && data.testimonials.items.length > 0),
  },
  pricing: {
    Component: Pricing,
    getProps: (data) => ({
      tiers: data.pricing?.tiers,
    }),
    hasData: (data) => Boolean(data.pricing?.tiers && data.pricing.tiers.length > 0),
  },
  faq: {
    Component: FAQ,
    getProps: () => ({}),
    hasData: () => true,
  },
  finalCTA: {
    Component: FinalCTA,
    getProps: () => ({}),
    hasData: () => true,
  },
  appScreenshots: {
    Component: AppScreenshots,
    getProps: (data) => ({
      items: data.appScreenshots?.items,
    }),
    hasData: (data) => Boolean(data.appScreenshots?.items && data.appScreenshots.items.length > 0),
  },
  featureSpotlight: {
    Component: FeatureSpotlight,
    getProps: (data) => ({
      features: data.featureSpotlight?.features,
    }),
    hasData: (data) =>
      Boolean(data.featureSpotlight?.features && data.featureSpotlight.features.length > 0),
  },
  appDemoVideo: {
    Component: AppDemoVideo,
    getProps: (data) => ({
      videoId: data.appDemoVideo?.videoId,
      thumbnail: data.appDemoVideo?.thumbnail,
    }),
    hasData: (data) => Boolean(data.appDemoVideo?.videoId),
  },
  appStoreDownload: {
    Component: AppStoreDownload,
    getProps: (data) => ({
      appStoreUrl: data.appStoreDownload?.appStoreUrl,
      playStoreUrl: data.appStoreDownload?.playStoreUrl,
      qrCodeImage: data.appStoreDownload?.qrCodeImage,
    }),
    hasData: (data) =>
      Boolean(data.appStoreDownload?.appStoreUrl || data.appStoreDownload?.playStoreUrl),
  },
  integrationLogos: {
    Component: IntegrationLogos,
    getProps: (data) => ({
      logos: data.integrationLogos?.logos,
    }),
    hasData: (data) =>
      Boolean(data.integrationLogos?.logos && data.integrationLogos.logos.length > 0),
  },
};

export function LandingPage() {
  const { t } = useTranslation("landing");

  // Get section order from seed data, default to empty array if not defined
  const sectionOrder = seedData.sectionOrder ?? [];

  return (
    <div className="relative min-h-screen">
      {/* Core section: Navbar (always rendered) */}
      <Navbar companyName={t("common:org.name")} ctaText={t("hero.primaryCTA")} />

      <main>
        {/* Core section: Hero (always rendered) */}
        <Hero />

        {/**
         * Conditional Section Rendering
         *
         * Iterates over sectionOrder and renders each section if:
         * 1. The section key exists in the component mapping
         * 2. The section has data in seedData (checked via hasData)
         *
         * This allows the landing page to be fully data-driven:
         * - Remove a section: delete its key from sectionOrder
         * - Reorder sections: change the order in sectionOrder array
         * - Add a section: add its key and corresponding data
         */}
        {sectionOrder.map((sectionKey) => {
          const sectionConfig = sectionComponents[sectionKey];

          // Skip if section component doesn't exist or has no data
          if (!sectionConfig || !sectionConfig.hasData(seedData)) {
            return null;
          }

          const { Component, getProps } = sectionConfig;
          const props = getProps(seedData);

          // Skip if props couldn't be extracted
          if (!props) {
            return null;
          }

          return <Component key={sectionKey} {...props} />;
        })}
      </main>

      {/* Core section: Footer (always rendered) */}
      <Footer navigation={seedData.footer.navigation} social={seedData.footer.social} />
    </div>
  );
}

export default LandingPage;
